"use client";

import React, { useCallback, useMemo } from "react";
import { Select } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Drawer, Button } from "@mantine/core";
import { ResponsiveCommandSelect } from "@/components/ui/responsive-select/ResponsiveCommandSelect";

// Types
import type { DetailActionProps, BaseEntity } from "../types/detailPage";

// ============================================================================
// STATUS DROPDOWN ACTION COMPONENT
// ============================================================================

interface StatusDropdownActionProps<T extends BaseEntity> extends DetailActionProps<T> {
  /** Status options configuration */
  statusOptions?: Array<{ value: string; label: string }>;

  /** Current status field name */
  statusField?: string;

  /** Display field name for context */
  displayField?: string;
}

/**
 * Reusable Status Dropdown Action Component
 * Can be used for any entity with a status field (contacts, tickets, tasks, etc.)
 */
export function StatusDropdownAction<T extends BaseEntity>({
  action,
  item,
  context,
  loading = false,
  statusOptions = [
    { value: "lead", label: "Lead" },
    { value: "prospect", label: "Prospect" },
    { value: "customer", label: "Customer" },
    { value: "churned", label: "Churned" },
    { value: "inactive", label: "Inactive" },
  ],
  statusField = "status",
  displayField = "name",
}: StatusDropdownActionProps<T>) {
  const [opened, { open, close }] = useDisclosure(false);

  const isTicketDetail = context.pageConfig?.entityName === "tickets";

  // Get current status value
  const currentStatus = (item as any)[statusField] || "";
  const itemName = (item as any)[displayField] || "this item";

  const selectOptions = useMemo(
    () =>
      statusOptions.map((opt) => ({
        value: opt.value,
        label: opt.label,
        searchableText: opt.label,
      })),
    [statusOptions]
  );

  // Handle status change
  const handleStatusChange = useCallback(
    async (newStatus: string) => {
      try {
        await context.onUpdateItem({
          [statusField]: newStatus,
        } as Partial<T>);

        context.onToast("Status updated successfully", "success");
        close();
      } catch (error) {
        console.error("Failed to update status:", error);
        context.onToast("Failed to update status", "error");
      }
    },
    [context, statusField, close]
  );

  return (
    <>
      {/* Desktop: expand-inline on ticket detail, otherwise Mantine Select */}
      <div className="hidden md:block">
        {isTicketDetail ? (
          <ResponsiveCommandSelect
            value={currentStatus}
            onValueChange={handleStatusChange}
            options={selectOptions}
            placeholder="Change status"
            disabled={loading}
            className="min-w-[140px]"
            expandInline
            expandInlineVariant="floating"
          />
        ) : (
          <Select
            value={currentStatus}
            onChange={(value) => value && handleStatusChange(value)}
            data={statusOptions}
            placeholder="Change status"
            disabled={loading}
            size="sm"
            style={{ width: 140 }}
          />
        )}
      </div>

      {/* Mobile: Button that opens drawer */}
      <div className="md:hidden">
        <Button variant="outline" size="sm" onClick={open} disabled={loading}>
          {statusOptions.find((opt) => opt.value === currentStatus)?.label || "Status"}
        </Button>
      </div>

      {/* Mobile Status Selection Drawer */}
      <Drawer opened={opened} onClose={close} title={"Change Status"}>
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Update status for {itemName}</p>

          <div className="space-y-2">
            {statusOptions.map((option) => (
              <Button
                key={option.value}
                variant={currentStatus === option.value ? "filled" : "light"}
                fullWidth
                onClick={() => handleStatusChange(option.value)}
                justify="start"
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>
      </Drawer>
    </>
  );
}

// Export with generic type
export { StatusDropdownAction as StatusDropdownActionComponent };
